/**
 * One place that sizes a Cloudinary delivery URL for the slot it is rendered in.
 *
 * Store images arrive in three shapes and each needs a different rewrite:
 * a stored lifestyle photo has no transformation at all, a design preview
 * carries `c_limit,w_<n>`, and a garment mockup opens with the `c_fill` that
 * every `fl_relative` layer after it is measured against, so that pair has to
 * be scaled together. Anything else is handed back untouched rather than
 * guessed at.
 */
const UPLOAD = "/image/upload/";

const MIN_WIDTH = 64;

const MAX_WIDTH = 2000;

export const THUMB_WIDTH = 128;

export const CARD_WIDTHS = [360, 560, 760] as const;

export const GALLERY_WIDTHS = [600, 900, 1200] as const;

export const HERO_WIDTHS = [640, 1024, 1280, 1600] as const;

export const CHIP_WIDTHS = [96, 144] as const;

/**
 * What the warm job pre-builds. Not every width in every ladder: Cloudinary
 * charges a derivation for each one, so this is the grid ladder every visitor
 * pays for plus the item page's main image, and the rest (hero 640/1024/1600,
 * the 192 thumb) is left to warm itself on first request.
 */
export const WARMED_MOCKUP_WIDTHS = [...CARD_WIDTHS, GALLERY_WIDTHS[GALLERY_WIDTHS.length - 1]] as const;

export const WARMED_LIFESTYLE_WIDTHS = [THUMB_WIDTH, CARD_WIDTHS[1], GALLERY_WIDTHS[1], GALLERY_WIDTHS[2]] as const;

function clampWidth(width: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));
}

function isCloudinary(url: string): boolean {
  return url.startsWith("https://res.cloudinary.com/") && url.includes(UPLOAD);
}

export function merchImageUrl(url: string | null | undefined, width: number): string | null {
  if (!url || !isCloudinary(url)) return url ?? null;
  const target = clampWidth(width);
  const index = url.indexOf(UPLOAD) + UPLOAD.length;
  const head = url.slice(0, index);
  const rest = url.slice(index);

  const fill = rest.match(/^c_fill,g_center,w_(\d+),h_(\d+)\//);
  if (fill) {
    const currentWidth = Number(fill[1]);
    const currentHeight = Number(fill[2]);
    if (currentWidth > 0 && currentHeight > 0) {
      // A mockup is cropped back in by `c_crop,w_<zoom>` at the end, so the
      // delivered image is narrower than the c_fill. Ask for the width that
      // survives that crop, or the srcset descriptor lies and browsers pick
      // a candidate one step too small.
      const crop = rest.match(/c_crop,w_(0?\.\d+),h_0?\.\d+,g_center\//);
      const zoom = crop ? Number(crop[1]) : 1;
      const fillWidth = Math.round(target / (zoom > 0 && zoom <= 1 ? zoom : 1));
      const height = Math.round((fillWidth * currentHeight) / currentWidth);
      return `${head}c_fill,g_center,w_${fillWidth},h_${height}/${rest.slice(fill[0].length)}`;
    }
  }

  const limit = rest.match(/^c_limit,w_(\d+)([^/]*)\//);
  if (limit) return `${head}c_limit,w_${target}${limit[2]}/${rest.slice(limit[0].length)}`;

  if (/^[a-z]_[^/]*\//i.test(rest)) return url;

  return `${head}c_limit,w_${target},f_auto,q_auto/${rest}`;
}

export function merchImageSrcSet(url: string | null | undefined, widths: readonly number[]): string | undefined {
  if (!url || !isCloudinary(url)) return undefined;
  const entries = [...new Set(widths.map(clampWidth))]
    .sort((left, right) => left - right)
    .map((width) => {
      const sized = merchImageUrl(url, width);
      return sized ? `${sized} ${width}w` : null;
    })
    .filter((entry): entry is string => Boolean(entry));
  return entries.length > 1 ? entries.join(", ") : undefined;
}

export interface SizedImage {
  src: string | null;
  srcSet?: string;
  sizes?: string;
}

export function sizedMerchImage(url: string | null | undefined, widths: readonly number[], sizes?: string): SizedImage {
  const largest = Math.max(...widths);
  return { src: merchImageUrl(url, largest), srcSet: merchImageSrcSet(url, widths), sizes };
}
